<template>
  <div class="hist-cell" :class="[cellClass, { 'hist-cell-total': isTotal }]">
    <!-- Línea 1: Producción H / IA / % -->
    <div class="hist-cell-row hist-cell-prod">
      <span class="hist-cell-pill hist-cell-pill-h" :title="`${h} spots producidos por humanos`">
        <span class="hist-cell-dot hist-cell-dot-h"></span>
        <span class="hist-cell-num">{{ h }}</span>
      </span>
      <span class="hist-cell-pill hist-cell-pill-ia" :title="`${i} spots producidos por IA`">
        <span class="hist-cell-dot hist-cell-dot-ia"></span>
        <span class="hist-cell-num">{{ i }}</span>
      </span>
      <span class="hist-cell-pct" :class="pctClass" :title="pctTitle">{{ pctLabel }}</span>
    </div>

    <!-- Línea 2: Barra de progreso (sin texto encima) -->
    <div class="hist-cell-bar" :title="pctTitle">
      <div class="hist-cell-bar-fill" :style="{ width: barWidth + '%' }"></div>
    </div>

    <!-- Línea 3: Meta (compacto, solo si hay) -->
    <div v-if="metaLabel" class="hist-cell-row hist-cell-meta" :title="metaTitle">
      <span class="hist-cell-meta-label">Meta</span>
      <span v-if="metaH > 0" class="hist-cell-meta-num hist-cell-meta-h">{{ metaH }}</span>
      <span v-if="metaH > 0 && metaI > 0" class="hist-cell-meta-sep">+</span>
      <span v-if="metaI > 0" class="hist-cell-meta-num hist-cell-meta-ia">{{ metaI }}</span>
    </div>
    <div v-else class="hist-cell-row hist-cell-meta hist-cell-meta-empty">
      <span class="hist-cell-meta-empty-text">Sin meta</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'HistCanalCell',
  props: {
    data: { type: Object, required: true },
    isTotal: { type: Boolean, default: false },
  },
  computed: {
    h() { return Number(this.data.humanos ?? this.data.spots_humanos ?? 0); },
    i() { return Number(this.data.ia ?? this.data.spots_ia ?? 0); },
    metaH() { return Number(this.data.metaHumano ?? this.data.meta_humano ?? 0); },
    metaI() { return Number(this.data.metaIa ?? this.data.meta_ia ?? 0); },
    metaTotal() { return this.metaH + this.metaI; },
    logradosTotal() { return this.h + this.i; },
    hasMeta() { return this.metaTotal > 0; },
    pctNum() {
      if (!this.hasMeta) return null;
      return Math.min(999, (this.logradosTotal / this.metaTotal) * 100);
    },
    pctLabel() {
      if (this.pctNum == null) return '—';
      return Math.round(this.pctNum) + '%';
    },
    barWidth() {
      if (this.pctNum == null) return 0;
      return Math.min(100, this.pctNum);
    },
    pctClass() {
      if (!this.hasMeta) return 'hist-cell-pct-no-meta';
      if (this.pctNum >= 100) return 'hist-cell-pct-green';
      if (this.pctNum >= 85) return 'hist-cell-pct-yellow';
      return 'hist-cell-pct-red';
    },
    pctTitle() {
      if (!this.hasMeta) return `Sin meta · producción: ${this.logradosTotal}`;
      return `Cumplimiento: ${Math.round(this.pctNum)}% (${this.logradosTotal} de ${this.metaTotal})`;
    },
    cellClass() {
      if (!this.hasMeta) return 'hist-cell-no-meta';
      if (this.pctNum >= 100) return 'hist-cell-green';
      if (this.pctNum >= 85) return 'hist-cell-yellow';
      return 'hist-cell-red';
    },
    metaLabel() {
      if (this.metaH > 0 && this.metaI > 0) return `Meta: ${this.metaH} humano + ${this.metaI} IA`;
      if (this.metaH > 0) return `Meta: ${this.metaH} humano`;
      if (this.metaI > 0) return `Meta: ${this.metaI} IA`;
      return null;
    },
    metaTitle() {
      if (this.metaH > 0 && this.metaI > 0) return `Meta humano: ${this.metaH}\nMeta IA: ${this.metaI}`;
      if (this.metaH > 0) return `Meta humano: ${this.metaH}`;
      if (this.metaI > 0) return `Meta IA: ${this.metaI}`;
      return '';
    },
  },
};
</script>

<style scoped>
.hist-cell {
  padding: 8px 8px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 120px;
  font-variant-numeric: tabular-nums;
  font-family: 'Poppins', 'Roboto', sans-serif;
  line-height: 1.3;
}

/* ── Línea 1: Producción ── */
.hist-cell-row {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.78rem;
}
.hist-cell-prod { justify-content: space-between; }
.hist-cell-pill {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 2px 6px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.78rem;
}
.hist-cell-pill-h { background: rgba(16, 185, 129, 0.14); color: #047857; }
.hist-cell-pill-ia { background: rgba(139, 92, 246, 0.14); color: #6d28d9; }
.hist-cell-dot {
  width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0;
}
.hist-cell-dot-h { background: #10b981; }
.hist-cell-dot-ia { background: #8b5cf6; }
.hist-cell-num { font-weight: 700; }

.hist-cell-pct {
  margin-left: auto;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}
.hist-cell-pct-green  { background: rgba(16, 185, 129, 0.15); color: #047857; }
.hist-cell-pct-yellow { background: rgba(245, 158, 11, 0.15); color: #b45309; }
.hist-cell-pct-red    { background: rgba(239, 68, 68, 0.15);  color: #b91c1c; }
.hist-cell-pct-no-meta { background: rgba(148, 163, 184, 0.18); color: #64748b; }

/* ── Línea 2: Barra ── */
.hist-cell-bar {
  position: relative;
  height: 6px;
  background: rgba(148, 163, 184, 0.25);
  border-radius: 3px;
  overflow: hidden;
}
.hist-cell-bar-fill {
  position: absolute;
  left: 0; top: 0; bottom: 0;
  transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 3px;
}
.hist-cell-green  .hist-cell-bar-fill { background: linear-gradient(90deg, #34d399, #10b981); }
.hist-cell-yellow .hist-cell-bar-fill { background: linear-gradient(90deg, #fbbf24, #f59e0b); }
.hist-cell-red    .hist-cell-bar-fill { background: linear-gradient(90deg, #f87171, #ef4444); }
.hist-cell-no-meta .hist-cell-bar-fill { background: rgba(148, 163, 184, 0.5); }

/* ── Línea 3: Meta ── */
.hist-cell-meta {
  font-size: 0.7rem;
  color: #64748b;
  font-weight: 500;
  gap: 4px;
}
.hist-cell-meta-label { color: #f59e0b; font-weight: 600; }
.hist-cell-meta-num { font-weight: 600; }
.hist-cell-meta-h { color: #047857; }
.hist-cell-meta-ia { color: #6d28d9; }
.hist-cell-meta-sep { color: #94a3b8; font-weight: 400; }
.hist-cell-meta-empty { justify-content: center; }
.hist-cell-meta-empty-text { color: #94a3b8; font-style: italic; font-weight: 400; font-size: 0.68rem; }

/* ── Variante Total ── */
.hist-cell-total {
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.04), rgba(16, 185, 129, 0.04));
  border-radius: 8px;
  padding: 10px 10px;
  min-width: 140px;
}
.hist-cell-total .hist-cell-row { font-size: 0.82rem; }
.hist-cell-total .hist-cell-pill { font-size: 0.82rem; padding: 3px 7px; }
.hist-cell-total .hist-cell-pct { font-size: 0.78rem; padding: 3px 10px; }
.hist-cell-total .hist-cell-meta { font-size: 0.72rem; }
.hist-cell-total .hist-cell-bar { height: 7px; }

/* ── Dark mode ── */
:global(.dark) .hist-cell-pill-h { background: rgba(16, 185, 129, 0.2); color: #6ee7b7; }
:global(.dark) .hist-cell-pill-ia { background: rgba(139, 92, 246, 0.22); color: #c4b5fd; }
:global(.dark) .hist-cell-dot-h { background: #34d399; }
:global(.dark) .hist-cell-dot-ia { background: #a78bfa; }

:global(.dark) .hist-cell-pct-green  { background: rgba(16, 185, 129, 0.25); color: #6ee7b7; }
:global(.dark) .hist-cell-pct-yellow { background: rgba(245, 158, 11, 0.25); color: #fcd34d; }
:global(.dark) .hist-cell-pct-red    { background: rgba(239, 68, 68, 0.25);  color: #fca5a5; }
:global(.dark) .hist-cell-pct-no-meta { background: rgba(148, 163, 184, 0.2); color: #a1a1aa; }

:global(.dark) .hist-cell-bar { background: rgba(148, 163, 184, 0.2); }
:global(.dark) .hist-cell-green  .hist-cell-bar-fill { background: linear-gradient(90deg, #34d399, #10b981); }
:global(.dark) .hist-cell-yellow .hist-cell-bar-fill { background: linear-gradient(90deg, #fbbf24, #f59e0b); }
:global(.dark) .hist-cell-red    .hist-cell-bar-fill { background: linear-gradient(90deg, #f87171, #ef4444); }
:global(.dark) .hist-cell-no-meta .hist-cell-bar-fill { background: rgba(148, 163, 184, 0.5); }

:global(.dark) .hist-cell-meta-label { color: #fbbf24; }
:global(.dark) .hist-cell-meta-h { color: #6ee7b7; }
:global(.dark) .hist-cell-meta-ia { color: #c4b5fd; }
:global(.dark) .hist-cell-meta-sep { color: #6b7280; }
:global(.dark) .hist-cell-meta { color: #94a3b8; }
:global(.dark) .hist-cell-meta-empty-text { color: #71717a; }

:global(.dark) .hist-cell-total {
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(16, 185, 129, 0.1));
}
</style>
